import { Resources } from 'constants/resources';

import React, { useRef } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { useCreateCommission } from 'hooks/queries/useCreateCommission';
import { useCreatePathWithQuery } from 'hooks/useCreatePathWithQuery';
import { useNavigateToRoot } from 'hooks/useNavigateToRoot';
import { useRecordModal } from 'hooks/useRecordModal';
import { COMMISSIONS_SCHEMA, LADDER_COMMISSIONS_SCHEMA } from 'pages/Commissions/schemas/schema';
import { transform } from 'pages/Commissions/utils/transform';
import { queryClient } from 'providers/queryClient';
import { HttpError, SimpleForm, useNotify } from 'react-admin';
import { FieldValues } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { CreatePage } from 'shared/react-admin/Pages/CreatePage/CreatePage';
import { CommissionsCreateApprovedSuccessDialog } from 'shared/widgets/CommissionsCreateApprovedSuccessDialog/CommissionsCreateApprovedSuccessDialog';
import { CommissionsCreateSuccessDialog } from 'shared/widgets/CommissionsCreateSuccessDialog/CommissionsCreateSuccessDialog';
import { Commission } from 'types/embedded/commission';
import { getSubmissionErrors } from 'utils/getSubmissionErrors';

import { CommissionsCreateForm, CommissionsCreateFormValues } from './CommissionsCreateForm';
import { CommissionsCreateToolbar } from './CommissionsCreateToolbar';
import { transformLadder } from './utils/transformLadder';

export const CommissionsCreate: React.FC = () => {
  const { toRoot } = useNavigateToRoot();
  const { createPathWithQuery } = useCreatePathWithQuery();
  const navigate = useNavigate();

  const target = useRef<'created' | undefined>();
  const modalApproved = useRecordModal<Commission>();
  const modalNew = useRecordModal<Commission>();
  const notify = useNotify();

  const { createCommissionAsync } = useCreateCommission();

  const onSubmit = async (data: CommissionsCreateFormValues) => {
    const transformedData = data.isLadder ? transformLadder(data) : transform(data);

    try {
      const result = await createCommissionAsync({
        data: transformedData,
        targetStatus: target.current,
      });

      await queryClient.invalidateQueries({ queryKey: [Resources.Commissions] });

      target.current
        ? modalNew.handleOpenRecordModal(result)
        : modalApproved.handleOpenRecordModal(result);
    } catch (error) {
      notify(`Ошибка: ${(error as Error)?.message || 'ошибка сервера'}`, {
        type: 'error',
        autoHideDuration: 3000,
      });

      return getSubmissionErrors(error as HttpError);
    }
  };

  return (
    <CreatePage listName="Комиссии" title="Создание новой комиссии">
      <SimpleForm
        onSubmit={onSubmit as any}
        resolver={(values, context, options) =>
          yupResolver<FieldValues>(
            values.isLadder ? LADDER_COMMISSIONS_SCHEMA : COMMISSIONS_SCHEMA,
          )(values, context, options)
        }
        toolbar={false}
      >
        <CommissionsCreateForm />
        <CommissionsCreateToolbar
          onCreate={() => (target.current = 'created')}
          onSaveAndApprove={() => (target.current = undefined)}
        />
      </SimpleForm>
      <CommissionsCreateSuccessDialog
        commissionName={modalNew.record?.name || ''}
        onAgreement={() =>
          navigate(
            createPathWithQuery({
              resource: Resources.Commissions,
              id: modalNew.record?.['@id'],
              type: 'show',
            }),
          )
        }
        onClose={() => {
          modalNew.handleCloseRecordModal();
          toRoot();
        }}
        open={modalNew.openRecordModal}
      />
      <CommissionsCreateApprovedSuccessDialog
        commissionName={modalApproved.record?.name || ''}
        onClose={() => {
          modalApproved.handleCloseRecordModal();
          toRoot();
        }}
        open={modalApproved.openRecordModal}
      />
    </CreatePage>
  );
};
